import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { T, RADIUS } from '@/lib/tokens';
import logoOnWhite from '@/assets/logo-on-white.svg';

/* ── Scoped hover styles (inline CSS cannot express :hover) ─────────────── */
const LEGAL_STYLES = `
  .legal-content a:hover { text-decoration: underline !important; }
`;

/* ── Helper sub-components ──────────────────────────────────────────────── */

export const LegalH2 = ({ children }: { children: ReactNode }) => (
  <h2
    style={{
      fontSize: 17,
      fontWeight: 700,
      color: T.text,
      marginTop: 28,
      marginBottom: 12,
      lineHeight: 1.4,
    }}
  >
    {children}
  </h2>
);

export const LegalH3 = ({ children }: { children: ReactNode }) => (
  <h3
    style={{
      fontSize: 15,
      fontWeight: 700,
      color: T.text,
      marginTop: 20,
      marginBottom: 8,
      lineHeight: 1.4,
    }}
  >
    {children}
  </h3>
);

export const LegalTable = ({ children }: { children: ReactNode }) => (
  <div style={{ overflowX: 'auto', marginBottom: 16 }}>
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      {children}
    </table>
  </div>
);

export const Th = ({ children }: { children: ReactNode }) => (
  <th
    style={{
      background: T.surfaceSubtle,
      border: `1px solid ${T.border}`,
      padding: '8px 12px',
      textAlign: 'left',
      fontWeight: 600,
      color: T.text,
    }}
  >
    {children}
  </th>
);

export const Td = ({ children }: { children: ReactNode }) => (
  <td
    style={{
      border: `1px solid ${T.border}`,
      padding: '8px 12px',
      verticalAlign: 'top',
      color: T.text,
    }}
  >
    {children}
  </td>
);

export const LegalLink = ({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) => (
  <a
    href={href}
    style={{ color: T.accent, textDecoration: 'none' }}
    target={href.startsWith('http') ? '_blank' : undefined}
    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
  >
    {children}
  </a>
);

/* ── Main layout ────────────────────────────────────────────────────────── */

interface LegalPageLayoutProps {
  title: string;
  effectiveDate?: string;
  lastUpdated?: string;
  version?: string;
  children: ReactNode;
}

const LegalPageLayout = ({
  title,
  effectiveDate,
  lastUpdated,
  version,
  children,
}: LegalPageLayoutProps) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div style={{ background: T.bg, minHeight: '100vh' }}>
      <style>{LEGAL_STYLES}</style>

      <div
        style={{
          maxWidth: 680,
          margin: '0 auto',
          padding: '0 20px 60px',
        }}
      >
        {/* Back button */}
        <button
          onClick={handleBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '20px 0 16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            color: T.accent,
            fontFamily: 'inherit',
          }}
        >
          ← Back
        </button>

        {/* Logo + wordmark */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            marginBottom: 28,
          }}
        >
          <img
            src={logoOnWhite}
            alt="Share Shack"
            style={{ width: 32, height: 32, display: 'block' }}
          />
          <span style={{ fontSize: 16, fontWeight: 600, color: T.text }}>
            Share Shack
          </span>
        </div>

        {/* Title */}
        <h1
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: T.text,
            marginBottom: 4,
            textAlign: 'center',
          }}
        >
          {title}
        </h1>

        {/* Metadata line */}
        {(effectiveDate || lastUpdated || version) && (
          <p
            style={{
              fontSize: 12,
              color: T.textSec,
              marginBottom: 24,
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            {effectiveDate && <>Effective Date: {effectiveDate}</>}
            {lastUpdated && (
              <>
                {effectiveDate && <br />}
                Last Updated: {lastUpdated}
              </>
            )}
            {version && (
              <>
                {(effectiveDate || lastUpdated) && <br />}
                Version: {version}
              </>
            )}
          </p>
        )}

        {/* Content */}
        <div
          className="legal-content"
          style={{
            fontSize: 14,
            lineHeight: 1.65,
            color: T.text,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default LegalPageLayout;
